/**
 * Host side of the Pyodide runner: owns the worker, enforces a time limit, and
 * restarts the worker when a program will not stop.
 */

import {
  answer,
  cancel,
  createInputChannel,
  interactiveInputAvailable,
} from "./inputChannel";
import type { InputChannel } from "./inputChannel";
import type { Runner, RunResult, StdinProvider } from "./types";
import { truncateDetail } from "./types";

/** The slice of `Worker` used here, so tests can supply a fake one. */
export interface WorkerLike {
  postMessage(message: unknown): void;
  terminate(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type WorkerFactory = () => WorkerLike;

/**
 * How long a learner's program may run before we assume it is stuck.
 *
 * Generous on purpose: a slow loop that eventually finishes should not be killed,
 * but an infinite one has to be. Ten seconds is far longer than any exercise in
 * the graph needs and short enough that a stuck learner is not left staring.
 */
export const DEFAULT_TIMEOUT_MS = 10_000;

export const PYTHON_STARTER = `# Anything you type here runs in your browser.
print("hello")
`;

interface Pending {
  resolve: (result: RunResult) => void;
  timer: ReturnType<typeof setTimeout>;
  /** Budget left on the runaway-loop clock, banked while the program waits for a human. */
  remainingMs: number;
  /** When the current stretch of *computing* began. */
  startedAt: number;
  kill: () => void;
}

export class PyodideRunner implements Runner {
  readonly id = "pyodide";
  readonly language = "python";
  readonly runLabel = "Run";
  readonly starterCode = PYTHON_STARTER;
  readonly canStop = false;

  private worker: WorkerLike | null = null;
  private loading: Promise<void> | null = null;
  private pending = new Map<number, Pending>();
  /** The shared buffer for the run in flight, when it can ask questions. */
  private channel: InputChannel | null = null;
  /** Told when a program pauses to ask, so the UI can show the question. */
  onAsk: ((prompt: string) => void) | null = null;
  /**
   * Whether `input()` will pause and ask, rather than needing answers up front.
   *
   * False on a page that is not cross-origin-isolated — plain HTTP without the
   * headers, or a browser without `credentialless`. Checked once here so the UI shows
   * one mechanism or the other rather than both.
   */
  readonly asksInteractively = interactiveInputAvailable();
  private nextId = 1;

  constructor(
    private readonly spawn: WorkerFactory,
    private readonly timeoutMs = DEFAULT_TIMEOUT_MS,
  ) {}

  private attach(): WorkerLike {
    const worker = this.spawn();
    worker.onmessage = (event) => this.onMessage(event.data);
    worker.onerror = (event) => {
      const message =
        typeof event === "object" && event && "message" in event
          ? String((event as { message: unknown }).message)
          : "the Python runtime crashed";
      this.failAll(message);
    };
    this.worker = worker;
    return worker;
  }

  private onMessage(data: unknown): void {
    if (typeof data !== "object" || data === null) return;
    const message = data as { type?: string; id?: number; result?: RunResult; prompt?: string };

    if (message.type === "asking") {
      // The program is paused inside input(). Stop the clock and show the question.
      this.pauseClock();
      this.onAsk?.(message.prompt ?? "");
      return;
    }

    if (message.type !== "result" || typeof message.id !== "number") return;

    const pending = this.pending.get(message.id);
    if (!pending) return; // a late reply from a run we already timed out
    clearTimeout(pending.timer);
    this.pending.delete(message.id);
    this.channel = null;
    pending.resolve(message.result ?? { outcome: "error", detail: "no result returned" });
  }

  private failAll(message: string): void {
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timer);
      this.pending.delete(id);
      pending.resolve({ outcome: "error", detail: truncateDetail(message) });
    }
  }

  /**
   * Throw the worker away and build a new one.
   *
   * The only way to stop a runaway Python program: there is no cooperative
   * interrupt without SharedArrayBuffer, and enabling that needs COEP headers
   * that would break Strudel's sample loading. Restarting costs a Pyodide reload
   * on the next run, which is the right trade against a frozen tab.
   */
  private restart(reason: string): void {
    this.worker?.terminate();
    this.worker = null;
    this.loading = null;
    this.failAll(reason);
  }

  async load(onProgress?: (message: string) => void): Promise<void> {
    if (this.loading) return this.loading;
    this.loading = new Promise<void>((resolve, reject) => {
      onProgress?.("Starting Python…");
      const worker = this.attach();
      const previous = worker.onmessage;
      worker.onmessage = (event) => {
        const message = event.data as { type?: string; message?: string };
        if (message?.type === "ready") {
          worker.onmessage = previous;
          onProgress?.("Python ready");
          resolve();
        } else if (message?.type === "failed") {
          worker.onmessage = previous;
          reject(new Error(message.message ?? "Python failed to start"));
        } else {
          previous?.(event);
        }
      };
      worker.postMessage({ type: "load" });
    });
    return this.loading;
  }

  async run(code: string, stdin?: StdinProvider): Promise<RunResult> {
    await this.load();
    const worker = this.worker;
    if (!worker) return { outcome: "error", detail: "Python is not running" };

    const interactive = interactiveInputAvailable();

    // Only used on the fallback path. When the program can ask questions as it runs
    // there is nothing to collect up front.
    const answers: string[] = [];
    if (!interactive && stdin) {
      for (let i = 0; i < 100; i += 1) {
        const answer = stdin("");
        if (answer === null) break;
        answers.push(answer);
      }
    }

    const channel = interactive ? createInputChannel() : null;
    this.channel = channel;

    const id = this.nextId++;
    return new Promise<RunResult>((resolve) => {
      const kill = () => {
        this.pending.delete(id);
        this.channel = null;
        this.restart(
          `That ran for more than ${Math.round(this.timeoutMs / 1000)} seconds and was ` +
            `stopped. Is something looping forever? Check that whatever the loop is ` +
            `waiting for actually changes inside it.`,
        );
        resolve({
          outcome: "error",
          detail:
            `Stopped after ${Math.round(this.timeoutMs / 1000)} seconds. ` +
            `Is something looping forever?`,
        });
      };

      // The clock measures *computing*, not waiting. A learner who takes half a
      // minute deciding what to type is not a runaway loop, and killing their program
      // for thinking would teach exactly the wrong lesson. So the remaining budget is
      // banked when the program pauses to ask, and restarted when the answer arrives.
      // A genuine infinite loop still dies, because it never pauses.
      const entry: Pending = {
        resolve,
        timer: setTimeout(kill, this.timeoutMs),
        remainingMs: this.timeoutMs,
        startedAt: Date.now(),
        kill,
      };
      this.pending.set(id, entry);
      worker.postMessage({
        type: "run",
        id,
        code,
        stdin: answers,
        ...(channel ? { inputBuffer: channel.buffer } : {}),
      });
    });
  }

  /** Hand the waiting program an answer, and restart its clock. */
  answerInput(text: string): void {
    const channel = this.channel;
    if (!channel) return;
    answer(channel, text);
    this.resumeClock();
  }

  /** Called when the worker says the program has paused to ask something. */
  private pauseClock(): void {
    for (const entry of this.pending.values()) {
      clearTimeout(entry.timer);
      entry.remainingMs = Math.max(250, entry.remainingMs - (Date.now() - entry.startedAt));
    }
  }

  private resumeClock(): void {
    for (const entry of this.pending.values()) {
      entry.startedAt = Date.now();
      entry.timer = setTimeout(entry.kill, entry.remainingMs);
    }
  }

  async stop(): Promise<void> {
    // If the program is parked in Atomics.wait, wake it first. Terminating a blocked
    // worker does work, but cancelling means `input()` sees EOF and the learner gets a
    // real Python error rather than a silent disappearance.
    if (this.channel) {
      cancel(this.channel);
      this.channel = null;
    }
    // Python has nothing running in the background; stopping means killing a
    // program mid-flight, which is what the timeout already does.
    this.restart("Stopped.");
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.loading = null;
    this.pending.clear();
  }
}

/** Production factory: Vite compiles the worker and hashes its URL. */
export function createPyodideRunner(timeoutMs = DEFAULT_TIMEOUT_MS): PyodideRunner {
  return new PyodideRunner(
    () =>
      new Worker(new URL("./pyodide.worker.ts", import.meta.url), {
        type: "module",
      }) as unknown as WorkerLike,
    timeoutMs,
  );
}
